import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  CaretLeft, CaretRight, Calendar as CalIcon, ListDashes, User,
  CalendarBlank, GridFour, FunnelSimple, Clock, Eye, EyeSlash, Package,
} from "@phosphor-icons/react";
import MonthView from "./agenda/MonthView";
import PlanningView from "./agenda/PlanningView";
import GrilleHoraireView from "./agenda/GrilleHoraireView";
import ListView from "./agenda/ListView";
import SupplierPickupsCard from "@/components/SupplierPickupsCard";
import { DAY_MS, startOfWeek, startOfMonth, isoDay, isoWeek } from "./agenda/dateHelpers";

const CHANTIER_TYPES = [
  { key: "electricite", label: "Électricité", color: "#F59E0B" },
  { key: "climatisation", label: "Climatisation", color: "#38BDF8" },
  { key: "video", label: "Vidéosurveillance", color: "#8B5CF6" },
  { key: "domotique", label: "Domotique", color: "#10B981" },
];

/**
 * Agenda root orchestrator — owns date range + data loading, delegates rendering
 * to a specialized view (Month, Planning, Grille horaire, List) mounted below.
 */
export default function Agenda() {
  const { isAdmin, user } = useAuth();
  const [mode, setMode] = useState(isAdmin ? "planning" : "list");
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [team, setTeam] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState(null);  // filtre par type de chantier
  const [hideDone, setHideDone] = useState(() => {
    try { return localStorage.getItem("bk.agenda.hideDone") === "1"; } catch { return false; }
  });
  const [chantiersMap, setChantiersMap] = useState({});
  const [clientsMap, setClientsMap] = useState({});
  const [leaves, setLeaves] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pickupsCount, setPickupsCount] = useState(0);

  // Wide-range pickup count for the "À prévoir" tab badge — polled every time
  // the agenda reloads so the badge stays in sync when items are marked done.
  useEffect(() => {
    let alive = true;
    const start = new Date();
    const end = new Date(start.getTime() + 60 * DAY_MS);
    const iso = (d) => d.toISOString().slice(0, 10);
    api.get("/supplier-pickups", { params: { start: iso(start), end: iso(end) } })
      .then((r) => { if (alive) setPickupsCount((r.data || []).length); })
      .catch(() => { if (alive) setPickupsCount(0); });
    return () => { alive = false; };
  }, [refreshKey]);

  const range = useMemo(() => {
    if (view === "month") {
      const monthStart = startOfMonth(anchor);
      const gridStart = startOfWeek(monthStart);
      const gridEnd = new Date(gridStart.getTime() + 42 * DAY_MS);
      const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
      return { start: gridStart, end: gridEnd, monthStart, monthEnd };
    }
    const s = startOfWeek(anchor);
    return { start: s, end: new Date(s.getTime() + 7 * DAY_MS) };
  }, [anchor, view]);

  const days = useMemo(() => {
    const n = view === "month" ? 42 : 7;
    return Array.from({ length: n }, (_, i) => new Date(range.start.getTime() + i * DAY_MS));
  }, [range, view]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const startIso = isoDay(range.start);
        const endIso = isoDay(range.end);
        const [u, i, c, cl, lv] = await Promise.all([
          api.get("/users"),
          api.get("/interventions", { params: { start: startIso, end: endIso + "T23:59" } }),
          api.get("/chantiers").catch(() => ({ data: [] })),
          api.get("/clients").catch(() => ({ data: [] })),
          api.get("/leave-requests", { params: { status: "approved", start: startIso, end: endIso } }).catch(() => ({ data: [] })),
        ]);
        setTeam(u.data);
        setInterventions(i.data);
        setChantiersMap(Object.fromEntries((c.data || []).map((x) => [x.id, x])));
        setClientsMap(Object.fromEntries((cl.data || []).map((x) => [x.id, x])));
        setLeaves(lv.data || []);
      } finally { setLoading(false); }
    })();
  }, [range.start, range.end, refreshKey]);

  const rangeLabel = view === "month"
    ? `${anchor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`
    : `S${isoWeek(anchor)} · ${range.start.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} → ${new Date(range.start.getTime() + 6 * DAY_MS).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`;

  const shift = (delta) => {
    if (view === "month") {
      setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1));
    } else {
      setAnchor(new Date(anchor.getTime() + delta * 7 * DAY_MS));
    }
  };

  const techTeam = team.filter((u) => (u.role === "admin" || u.role === "technician" || u.role === "associate" || u.role === "external" || u.role === "compta") && u.is_active !== false);
  // Type filter: only keep interventions whose chantier.type matches. Chantiers
  // without a type are hidden when a filter is active (so "Toutes" = all).
  const typeFilteredIvs = typeFilter
    ? interventions.filter((iv) => (chantiersMap[iv.chantier_id]?.type || null) === typeFilter)
    : interventions;
  // Apply "hide done" toggle globally (all views consume this filtered list)
  const visibleInterventions = hideDone ? typeFilteredIvs.filter((x) => x.status !== "done" && x.status !== "cancelled") : typeFilteredIvs;
  const toggleHideDone = () => setHideDone((v) => {
    const next = !v;
    try { localStorage.setItem("bk.agenda.hideDone", next ? "1" : "0"); } catch (e) { console.debug("[agenda] localStorage blocked:", e?.message || e); }
    return next;
  });
  const reload = () => setRefreshKey((k) => k + 1);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 hover:bg-white/10 transition-colors" data-testid="agenda-prev">
            <CaretLeft size={16} weight="bold" />
          </button>
          <button onClick={() => setAnchor(new Date())} className="text-xs uppercase tracking-widest text-white/70 px-3 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors" data-testid="agenda-today">Aujourd&apos;hui</button>
          <button onClick={() => shift(1)} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 hover:bg-white/10 transition-colors" data-testid="agenda-next">
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white/5 border border-white/10 rounded-full p-1" data-testid="agenda-view">
            <button onClick={() => setView("week")} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${view === "week" ? "bg-[#005CFF] text-white shadow" : "text-white/60 hover:text-white/90"}`} data-testid="view-week">
              <CalendarBlank size={13} weight="bold" /> Semaine
            </button>
            <button onClick={() => setView("month")} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${view === "month" ? "bg-[#005CFF] text-white shadow" : "text-white/60 hover:text-white/90"}`} data-testid="view-month">
              <GridFour size={13} weight="bold" /> Mois
            </button>
            <button
              onClick={() => setView("prevoir")}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${view === "prevoir" ? "bg-amber-500 text-black shadow" : "text-white/60 hover:text-white/90"}`}
              data-testid="view-prevoir"
              title="Matériel à récupérer chez fournisseur"
            >
              <Package size={13} weight="bold" /> À prévoir
              {pickupsCount > 0 && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full tabular-nums ${view === "prevoir" ? "bg-black/25 text-black" : "bg-amber-500/20 text-amber-300"}`}>
                  {pickupsCount}
                </span>
              )}
            </button>
          </div>
          {isAdmin && view === "week" && (
            <div className="flex bg-white/5 border border-white/10 rounded-full p-1" data-testid="agenda-mode">
              <button onClick={() => setMode("planning")} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${mode === "planning" ? "bg-[#005CFF] text-white shadow" : "text-white/60 hover:text-white/90"}`}>
                <User size={14} weight="bold" /> Planning
              </button>
              <button onClick={() => setMode("grille")} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${mode === "grille" ? "bg-[#005CFF] text-white shadow" : "text-white/60 hover:text-white/90"}`} data-testid="mode-grille">
                <Clock size={14} weight="bold" /> Grille
              </button>
              <button onClick={() => setMode("list")} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${mode === "list" ? "bg-[#005CFF] text-white shadow" : "text-white/60 hover:text-white/90"}`}>
                <ListDashes size={14} weight="bold" /> Liste
              </button>
            </div>
          )}
          <button
            onClick={toggleHideDone}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${hideDone ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-white/60 hover:border-white/25"}`}
            title={hideDone ? "Afficher aussi les interventions terminées" : "Cacher les interventions terminées"}
            data-testid="toggle-hide-done"
          >
            {hideDone ? <EyeSlash size={13} weight="bold" /> : <Eye size={13} weight="bold" />}
            {hideDone ? "Terminées cachées" : "Cacher terminées"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <CalIcon size={18} className="text-white/50" />
        <div className="font-display font-semibold text-lg capitalize" data-testid="agenda-range">
          {view === "prevoir" ? "À prévoir · 60 prochains jours" : rangeLabel}
        </div>
      </div>

      {isAdmin && view === "week" && mode === "planning" && (
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1" data-testid="type-filter-bar">
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/40 shrink-0">
            <FunnelSimple size={11} /> Type de chantier
          </span>
          <button
            onClick={() => setTypeFilter(null)}
            className={`shrink-0 text-xs px-3 py-1 rounded-full border transition-colors ${!typeFilter ? "bg-[#005CFF] border-[#005CFF] text-white" : "bg-white/5 border-white/10 text-white/60 hover:border-white/25"}`}
            data-testid="type-filter-all"
          >
            Tous
          </button>
          {CHANTIER_TYPES.map((t) => {
            const count = interventions.filter((iv) => chantiersMap[iv.chantier_id]?.type === t.key).length;
            const isOn = typeFilter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTypeFilter(isOn ? null : t.key)}
                className={`shrink-0 text-xs px-3 py-1 rounded-full border transition-colors flex items-center gap-1.5 ${isOn ? "text-white" : "text-white/60 hover:border-white/25"}`}
                style={isOn ? { background: t.color, borderColor: t.color } : { background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.1)" }}
                data-testid={`type-filter-${t.key}`}
              >
                <span>{t.label}</span>
                <span className={`text-[9px] px-1 rounded-full ${isOn ? "bg-white/25" : "bg-white/10"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="h-64 rounded-2xl bg-white/5 animate-pulse" />
      ) : view === "prevoir" ? (
        (() => {
          const today = new Date();
          const in60 = new Date(today.getTime() + 60 * DAY_MS);
          return (
            <SupplierPickupsCard
              start={isoDay(today)}
              end={isoDay(in60)}
              team={team}
              onCountChange={setPickupsCount}
            />
          );
        })()
      ) : view === "month" ? (
        <MonthView
          days={days}
          anchor={anchor}
          interventions={visibleInterventions}
          team={techTeam}
          chantiersMap={chantiersMap}
          onDayClick={(d) => { setAnchor(d); setView("week"); }}
          leaves={leaves}
          isAdmin={isAdmin}
          onChange={reload}
        />
      ) : mode === "planning" ? (
        <PlanningView team={techTeam} allTeam={techTeam} days={days} interventions={visibleInterventions} isAdmin={isAdmin} currentUser={user} onChange={reload} chantiersMap={chantiersMap} clientsMap={clientsMap} leaves={leaves} />
      ) : mode === "grille" ? (
        <GrilleHoraireView days={days} interventions={visibleInterventions} team={techTeam} chantiersMap={chantiersMap} onChange={reload} />
      ) : (
        <ListView days={days} interventions={visibleInterventions} team={team} refreshKey={range.start.toISOString()} chantiersMap={chantiersMap} />
      )}
    </div>
  );
}
